import { type Money, money } from "../money/money";
import { expectedCash } from "./expected-cash";

/**
 * X and Z readings.
 *
 * SC-006 requires a Z reading to reproduce identical figures on every
 * regeneration, across 30 consecutive trading days. That is achieved by
 * COMPUTING ONCE, FREEZING, AND THEREAFTER REPLAYING — not by recomputing
 * carefully. Recomputation would silently drift the moment a late sale arrives,
 * a price is corrected, or the tax regime changes, and a reading that quietly
 * changes is worse than no reading at all.
 *
 * An X reading is computed live and closes nothing (FR-037).
 */

export class ReadingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ReadingError";
  }
}

export type ReadingType = "x" | "z";

export interface ReadingTender {
  type: string;
  amount: Money;
  changeGiven: Money;
}

export interface ReadingSale {
  documentNumber: string;
  total: Money;
  subtotal: Money;
  taxAmount: Money;
  discountTotal: Money;
  statutoryDiscountTotal: Money;
  status: string;
  tenders: ReadingTender[];
}

export interface ReadingSource {
  shiftId: string;
  terminalId: string;
  businessDate: string;
  openingFloat: Money;
  sales: ReadingSale[];
  voids: Array<{ saleId: string; documentNumber: string; amount: Money }>;
  refunds: Array<{ saleId: string; amount: Money; isCash: boolean }>;
  cashMovements: Array<{ direction: "in" | "out"; amount: Money }>;
  /** Core cash-control counts (FR-107). */
  noSaleCount: number;
  failedDrawerReleaseCount: number;
}

export interface Reading {
  type: ReadingType;
  shiftId: string;
  terminalId: string;
  businessDate: string;

  transactionCount: number;
  grossSales: Money;
  netSales: Money;
  taxAmount: Money;
  discountTotal: Money;
  statutoryDiscountTotal: Money;

  voidCount: number;
  voidAmount: Money;
  refundCount: number;
  refundAmount: Money;

  tenderBreakdown: Record<string, number>;
  openingFloat: Money;
  expectedCash: Money;

  noSaleCount: number;
  failedDrawerReleaseCount: number;

  documentNumberFrom: string | null;
  documentNumberTo: string | null;
}

const CASH_TYPES = new Set(["cash"]);

export function computeReading(source: ReadingSource, type: ReadingType): Reading {
  const sales = source.sales;

  let grossSales = 0;
  let taxAmount = 0;
  let discountTotal = 0;
  let statutoryDiscountTotal = 0;
  const tenderBreakdown: Record<string, number> = {};
  let cashRetained = 0;

  // A voided sale stays in the day's gross and keeps its place in the document
  // range — it happened, and the number was issued. What it must NOT do is
  // leave money in the drawer expectation, because that money went back to the
  // customer. Reporting it as cash retained would make every close after a
  // voided cash sale look short by exactly that amount.
  const voidedDocuments = new Set(source.voids.map((v) => v.documentNumber));

  for (const sale of sales) {
    grossSales += sale.total;
    taxAmount += sale.taxAmount;
    discountTotal += sale.discountTotal;
    statutoryDiscountTotal += sale.statutoryDiscountTotal;

    if (voidedDocuments.has(sale.documentNumber)) continue;

    for (const tender of sale.tenders) {
      // Net of change: ₱200 tendered with ₱50 back is ₱150 in the drawer, and
      // reporting the gross would make the tender breakdown disagree with the
      // physical count.
      const net = tender.amount - tender.changeGiven;
      tenderBreakdown[tender.type] = (tenderBreakdown[tender.type] ?? 0) + net;
      if (CASH_TYPES.has(tender.type)) cashRetained += net;
    }
  }

  const voidAmount = source.voids.reduce((total, v) => total + v.amount, 0);
  const refundAmount = source.refunds.reduce((total, r) => total + r.amount, 0);
  const cashRefunds = source.refunds
    .filter((r) => r.isCash)
    .reduce((total, r) => total + r.amount, 0);

  const payIns = source.cashMovements
    .filter((m) => m.direction === "in")
    .reduce((total, m) => total + m.amount, 0);
  const payOuts = source.cashMovements
    .filter((m) => m.direction === "out")
    .reduce((total, m) => total + m.amount, 0);

  const documentNumbers = sales.map((s) => s.documentNumber).sort();

  return {
    type,
    shiftId: source.shiftId,
    terminalId: source.terminalId,
    businessDate: source.businessDate,

    transactionCount: sales.length,
    grossSales: money(grossSales),
    netSales: money(grossSales - voidAmount - refundAmount),
    taxAmount: money(taxAmount),
    discountTotal: money(discountTotal),
    statutoryDiscountTotal: money(statutoryDiscountTotal),

    voidCount: source.voids.length,
    voidAmount: money(voidAmount),
    refundCount: source.refunds.length,
    refundAmount: money(refundAmount),

    tenderBreakdown,
    openingFloat: source.openingFloat,
    expectedCash: expectedCash({
      openingFloat: source.openingFloat,
      cashSales: money(cashRetained),
      cashRefunds: money(cashRefunds),
      payIns: money(payIns),
      payOuts: money(payOuts),
    }),

    noSaleCount: source.noSaleCount,
    failedDrawerReleaseCount: source.failedDrawerReleaseCount,

    documentNumberFrom: documentNumbers[0] ?? null,
    documentNumberTo: documentNumbers[documentNumbers.length - 1] ?? null,
  };
}

/**
 * Freeze a Z reading for storage.
 *
 * Written ONCE. Everything afterwards reads this string, never the sales.
 */
export function freezeZReading(reading: Reading): string {
  if (reading.type !== "z") {
    throw new ReadingError("Only a Z reading is frozen; an X reading is always computed live");
  }
  return JSON.stringify(reading);
}

/** Required keys — a partial snapshot must fail loudly, not report zeroes. */
const REQUIRED_KEYS: Array<keyof Reading> = [
  "type",
  "shiftId",
  "businessDate",
  "transactionCount",
  "grossSales",
  "netSales",
  "expectedCash",
  "tenderBreakdown",
];

export function replayReading(frozen: string): Reading {
  let parsed: unknown;
  try {
    parsed = JSON.parse(frozen);
  } catch {
    throw new ReadingError("Stored reading is not valid JSON");
  }

  if (typeof parsed !== "object" || parsed === null) {
    throw new ReadingError("Stored reading is not an object");
  }

  for (const key of REQUIRED_KEYS) {
    if (!(key in parsed)) {
      throw new ReadingError(`Stored reading is missing "${String(key)}"`);
    }
  }

  return parsed as Reading;
}
